import { useEffect, useRef, useState } from "react";
import { AviTracerApi } from "../../../avitracerApi";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Col, Container, Row } from "react-bootstrap";
import { Document, Page, pdfjs } from 'react-pdf'
import { useWindowSize } from "usehooks-ts";
import SignatureCanvas from 'react-signature-canvas'
import { Player } from "@lottiefiles/react-lottie-player";
import LoadingComponent from "../../../components/LoadingComponent";
import { FlightCustomDocsType } from "../../../models/flight";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { refreshFlight } from "../../../store/flightSlice";
import { selectedOrganization } from "../../../store/organizationsSlice";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.6.172/pdf.worker.js`;

function FlightDocSignPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedOrg = useAppSelector(selectedOrganization);

  const { width } = useWindowSize();

  const { flightId, docType } = useParams<{flightId: string, docType: FlightCustomDocsType}>();

  const [ pdfBuffer, setPdfBuffer ] = useState(null);
  const [ pdfDocProxy, setPdfDocProxy] = useState<pdfjs.PDFDocumentProxy>();
  const [ docSigned, setDocSigned ] = useState(false)
  const [ selectedAcceptanceStatus, setSelectedAcceptanceStatus ] = useState(false)
  
  const [errorStr, setErrorStr] = useState<string | null>(null);

  const signatureRef = useRef<SignatureCanvas>(null);
  const [inSignMode, setInSignMode] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  //The ref blocks a second tap before the re-render disables the button
  const uploadingRef = useRef(false);
  const [isPreparingSign, setIsPreparingSign] = useState(false);
  const [uploadOk, setUploadOk] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printDone, setPrintDone] = useState(false);

  useEffect(() => {
    const getPdf = async () => {
      try {
      const pdfB = await AviTracerApi.getCustomPdfDoc(flightId!, docType!);
      setPdfBuffer(pdfB);
    } catch (e: any) {
      if (e === "SIGNED") {
        setErrorStr("Document has already been signed.")
        setDocSigned(true)
      } else {
        setErrorStr(e);
      }
    }
    };

    getPdf();
  }, [docType, flightId]);

  useEffect(() => {
    const getPdf = async () => {
      if (docSigned) {
        const pdfB = await AviTracerApi.getCustomPdfDoc(flightId!, docType!, true);
        setPdfBuffer(pdfB);
      }
    };
    getPdf();
  }, [docSigned, docType, flightId]);

  const submitSignature = async () => {
    if (uploadingRef.current) return;
    if (signatureRef.current!.isEmpty()) {
      setErrorStr("Please sign in the above area.");
      return;
    }
    setErrorStr(null);
    
    uploadingRef.current = true;
    setIsUploading(true);
    try {
      const signatureDataUrl = signatureRef.current!.getTrimmedCanvas().toDataURL();
      await AviTracerApi.signCustomPdfDoc(flightId!, docType!, signatureDataUrl, selectedAcceptanceStatus);
      dispatch(refreshFlight(flightId!));
      setUploadOk(true)
    } catch (e: any) {
      setErrorStr(typeof e === "string" ? e : "Could not sign the document. Please try again.");
    } finally {
      uploadingRef.current = false;
      setIsUploading(false);
    }
  };

  const downloadSignedDocPressed = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const pdfData = await AviTracerApi.getCustomPdfDoc(flightId!, docType!, true);
      const link = document.createElement('a');
      link.download = docType + ".pdf";
      link.href = window.URL.createObjectURL(new Blob([pdfData], { type: 'application/pdf' }));
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e: any) {
      setErrorStr(typeof e === "string" ? e : "Could not download the document. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  }

  const directPrintPressed = async () => {
    if (isPrinting) return;
    setIsPrinting(true);
    setErrorStr(null);
    try {
      await AviTracerApi.printFlightDoc(flightId!, docType!);
      setPrintDone(true);
    } catch (e: any) {
      setErrorStr(typeof e === "string" ? e : "Could not send the document to the print server.");
    } finally {
      setIsPrinting(false);
    }
  }

  const getCurrentAcceptanceStatusFromPdfAndGoToSign = async () => {
    if (!pdfDocProxy || isPreparingSign) return;
    setIsPreparingSign(true);
    try {
      const savedPdfData = await pdfDocProxy.saveDocument();

      const savedPdf = await pdfjs.getDocument(savedPdfData).promise;
      const obj = await savedPdf.getFieldObjects();

      if (obj && obj["acceptance"]?.length > 1){
        setSelectedAcceptanceStatus((obj["acceptance"].at(-1) as any).value === "yes");
      }
      
      setInSignMode(true);
    } finally {
      setIsPreparingSign(false);
    }
  }

  return (
    <>
      {!inSignMode && (
        <Container fluid={"xl"} style={{ paddingBottom: "120px" }}>
          <Row>
            <Col>
              {pdfBuffer ? (
                <Document
                  file={pdfBuffer}
                  loading={<LoadingComponent text="Loading document..." />}
                  onLoadSuccess={(d) => {
                    setPdfDocProxy(d);
                  }}
                >
                  <Page width={Math.min(width, 1280)} pageNumber={1} renderAnnotationLayer={true} renderForms={true} />
                </Document>
              ) : (
                <LoadingComponent text="Loading document..." />
              )}
            </Col>
          </Row>
          <Container className="fixed-bottom bg-light shadow" fluid>
            <Row className="justify-content-center py-3 ">
              {errorStr && (
                <>
                  <Col xs={10} lg={6}>
                    <Alert variant="danger">{errorStr}</Alert>
                  </Col>
                  <div className="w-100" />
                </>
              )}
              <Col xs={5} lg={3} xl={2}>
                <div className="d-grid">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      if (docSigned) {
                        navigate("../", { relative: "path" });
                      } else {
                        navigate("../edit", { relative: "path" });
                      }
                    }}
                  >
                    Edit document
                  </Button>
                </div>
              </Col>
              <Col xs={5} lg={3} xl={2}>
                <div className="d-grid">
                  <Button variant="dark" disabled={docSigned || errorStr !== null || !pdfDocProxy || isPreparingSign} onClick={getCurrentAcceptanceStatusFromPdfAndGoToSign}>
                    {docSigned ? "Already signed" : !pdfDocProxy && errorStr === null ? "Loading..." : "Sign document"}
                  </Button>
                </div>
              </Col>
            </Row>
          </Container>
        </Container>
      )}
      {uploadOk && (
        <Row className="justify-content-center text-center pt-5">
          <Col>
            <Player
              autoplay
              loop
              src="/assets/brief/lottie/documentOk.json"
              speed={1}
              background="transparent"
              style={{ height: "200px", width: "200px" }}
            />
            <div className="fs-5 fw-bold pb-4">Document signed successfully.</div>
            {docType === "soloSupCertificate" ? (
              <Button
                onClick={() => {
                  navigate("../../riskAssessment", {relative: "path"});
                }}
                variant="dark"
              >
                Go to Risk Assessment
              </Button>
            ) : (
              <>
                {docType === "trainingRecord" && (
                  <Button
                    variant="outline-dark"
                    className="fw-bold me-2"
                    disabled={isDownloading}
                    onClick={downloadSignedDocPressed}
                  >
                    {isDownloading ? "Preparing..." : "Download Training Record"}
                  </Button>
                )}
                {docType === "trainingRecord" && selectedOrg?.printServer?.enabled && (
                  <Button
                    variant="outline-dark"
                    className="fw-bold me-2"
                    disabled={isPrinting || printDone}
                    onClick={directPrintPressed}
                  >
                    {printDone ? "Sent to printer" : isPrinting ? "Sending..." : "Direct Print"}
                  </Button>
                )}
                <Button
                  onClick={() => {
                    navigate("/");
                  }}
                  variant="dark"
                >
                  Go to Flights
                </Button>
                {errorStr && <div className="text-danger pt-3">{errorStr}</div>}
              </>
            )}
          </Col>
        </Row>
      )}
      {inSignMode && !uploadOk && (
        <>
          <div className="vw-100 vh-100 bg-white">
            <div className="bg-white w-100 h-100">
              <SignatureCanvas minWidth={2} maxWidth={5} ref={signatureRef} dotSize={3} canvasProps={{ className: "w-100 h-100" }} />
            </div>
          </div>
          <Container className="fixed-bottom bg-light shadow" fluid>
            <Row className="justify-content-center py-3 ">
              {errorStr && (
                <>
                  <Col xs={10} lg={6}>
                    <Alert variant="danger">{errorStr}</Alert>
                  </Col>
                  <div className="w-100" />
                </>
              )}
              <Col xs={5} lg={3} xl={2}>
                <div className="d-grid">
                  <Button
                    variant="outline-secondary"
                    disabled={isUploading}
                    onClick={() => {
                      setInSignMode(false);
                      setErrorStr(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Col>
              <Col xs={5} lg={3} xl={2}>
                <div className="d-grid">
                  <Button variant="dark" disabled={isUploading} onClick={submitSignature}>
                    {!isUploading ? "Submit" : "Signing..."}
                  </Button>
                </div>
              </Col>
            </Row>
          </Container>
        </>
      )}
    </>
  );
}

export default FlightDocSignPage